import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { User, Building2, Search } from 'lucide-react';
import Pagination from './Pagination';
import ConfirmModal from './ConfirmModal';
import Toast from './Toast';

const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, member: null, action: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const fetchStaff = async (currentPage = 1) => {
    try {
      setLoading(true);
      const res = await api.get(`/api/admin/staff?page=${currentPage}&limit=10`);
      setStaff(res.data.data);
      setPaginationData(res.data.pagination);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching staff:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff(page);
  }, [page]);

  const filteredStaff = staff.filter(s =>
    s.name?.toLowerCase().includes(filter.toLowerCase()) ||
    s.email?.toLowerCase().includes(filter.toLowerCase()) ||
    s.phoneNumber?.toLowerCase().includes(filter.toLowerCase()) ||
    s.pharmacyName?.toLowerCase().includes(filter.toLowerCase())
  );

  const handleStatusToggle = (member) => {
    const newStatus = !member.isBlocked;
    const action = newStatus ? 'block' : 'unblock';
    
    setConfirmModal({ 
      isOpen: true, 
      member, 
      action,
      title: `${newStatus ? 'Block' : 'Unblock'} ${member.name}?`,
      message: `Are you sure you want to ${action} this staff member? ${newStatus ? 'They will be immediately locked out of the system.' : 'They will regain access to their account.'}`
    });
  };

  const executeStatusToggle = async () => {
    const { member, action } = confirmModal;
    const newStatus = action === 'block';
    
    setConfirmModal({ ...confirmModal, isOpen: false });

    try {
      await api.post(`/api/admin/staff/${member.id}/status`, { isBlocked: newStatus });
      setToast({ show: true, message: `Staff member ${newStatus ? 'blocked' : 'unblocked'} successfully!`, type: 'success' });
      fetchStaff(page);
    } catch (err) {
      setToast({ show: true, message: `Error: ${err.response?.data?.message || err.message}`, type: 'error' });
    }
  };

  return (
    <div className="space-y-8 sm:space-y-10">
      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast({ ...toast, show: false })} 
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.action === 'block' ? 'danger' : 'success'}
        confirmText={`Yes, ${confirmModal.action === 'block' ? 'Block' : 'Unblock'}`}
        onConfirm={executeStatusToggle}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Pharmacy Staff</h1>

        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search staff or pharmacy..."
            className="pl-10 pr-4 py-2.5 sm:py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-full transition-all"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {/* Table / Card View */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-left min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Staff Member</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Pharmacy</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Salary</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-500">Loading staff members...</td>
                </tr>
              ) : filteredStaff.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 sm:px-6 py-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="p-1.5 sm:p-2 bg-indigo-50 text-indigo-600 rounded-lg flex-shrink-0">
                        <User size={16} />
                      </div>
                      <span className="font-bold text-gray-900 text-sm sm:text-base">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <div className="text-sm">
                      <div className="text-gray-900 font-medium">{s.phoneNumber || 'No phone'}</div>
                      <div className="text-gray-500 truncate max-w-[160px]" title={s.email}>{s.email}</div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-gray-700">
                      <Building2 size={14} className="text-gray-400" />
                      <span className="font-medium">{s.pharmacyName || 'Unlinked'}</span>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-sm font-semibold text-gray-900">
                    ৳{s.monthlySalary?.toLocaleString() || 0}
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    {s.email?.startsWith('deleted') ? (
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase border whitespace-nowrap bg-gray-100 text-gray-700 border-gray-200">
                        Deleted
                      </span>
                    ) : (
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase border whitespace-nowrap ${!s.isBlocked ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                        {!s.isBlocked ? 'Active' : 'Blocked'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-sm text-gray-500">
                    {new Date(s.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-right">
                    {!s.email?.startsWith('deleted') && (
                      <button
                        onClick={() => handleStatusToggle(s)}
                        className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${!s.isBlocked ? 'text-red-600 bg-red-50 hover:bg-red-100' : 'text-green-600 bg-green-50 hover:bg-green-100'}`}
                      >
                        {s.isBlocked ? 'Unblock' : 'Block'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && filteredStaff.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                    No staff members found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="sm:hidden divide-y divide-gray-100">
          {loading ? (
            <div className="p-6 text-center text-gray-500">Loading staff members...</div>
          ) : filteredStaff.map((s) => (
            <div key={s.id} className="p-4 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                    <User size={18} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{s.name}</h4>
                    <p className="text-xs text-gray-500">{s.phoneNumber || s.email}</p>
                  </div>
                </div>
                {s.email?.startsWith('deleted') ? (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase border bg-gray-100 text-gray-700 border-gray-200">
                    Deleted
                  </span>
                ) : (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase border ${!s.isBlocked ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                    {!s.isBlocked ? 'Active' : 'Blocked'}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm border-t border-gray-50 pt-3">
                <div>
                  <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Pharmacy</p>
                  <p className="font-medium text-gray-700 truncate">{s.pharmacyName || 'Unlinked'}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider text-right">Monthly Salary</p>
                  <p className="font-bold text-gray-900 text-right">৳{s.monthlySalary?.toLocaleString() || 0}</p>
                </div>
              </div>
              
              {!s.email?.startsWith('deleted') && (
                <button
                  onClick={() => handleStatusToggle(s)}
                  className={`w-full mt-2 py-2 rounded-lg text-xs font-bold border transition-all ${!s.isBlocked ? 'text-red-600 border-red-100 bg-red-50 hover:bg-red-100' : 'text-green-600 border-green-100 bg-green-50 hover:bg-green-100'}`}
                >
                  {s.isBlocked ? 'Unblock Staff Member' : 'Block Staff Member'}
                </button>
              )}
            </div>
          ))}
          {!loading && filteredStaff.length === 0 && (
            <div className="p-6 text-center text-gray-500">No staff members found.</div>
          )}
        </div>

        <Pagination pagination={paginationData} onPageChange={setPage} />
      </div>
    </div>
  );
};

export default Staff;
